import { useState, useCallback } from 'react'
import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import Notification from '@/components/ui/Notification'
import { FormInput, FormCheckbox } from '@/components/ui/form'
import Button from '@/components/ui/Button'
import { Badge } from '@/components/catalyst/badge'
import { UserCircleIcon } from '@heroicons/react/24/solid'

// ─── Types ──────────────────────────────────────

interface ProfileProps {
  user: { id: number; name: string; email: string; role: string; created_at: string }
  notification_settings: {
    order_statuses: Record<string, boolean>
    vendor_po_statuses: Record<string, boolean>
    customer_po_statuses: Record<string, boolean>
  }
}

function humanize(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// ─── Component ──────────────────────────────────

export default function ProfileIndex({ user, notification_settings }: ProfileProps) {
  const [name, setName] = useState(user.name || '')
  const [email, setEmail] = useState(user.email || '')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [profileErrors, setProfileErrors] = useState<string[]>([])
  const [profileSaving, setProfileSaving] = useState(false)

  const [orderStatuses, setOrderStatuses] = useState(notification_settings.order_statuses)
  const [vendorPoStatuses, setVendorPoStatuses] = useState(notification_settings.vendor_po_statuses)
  const [customerPoStatuses, setCustomerPoStatuses] = useState(notification_settings.customer_po_statuses)
  const [notifSaving, setNotifSaving] = useState(false)

  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)

  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''
  const notify = useCallback((msg: string) => { setNotifMessage(msg); setShowNotif(true) }, [])

  // ─── Save profile ───────────────────────────
  const saveProfile = async () => {
    if (!currentPassword) { setProfileErrors(['Current password is required to confirm changes']); return }
    setProfileErrors([]); setProfileSaving(true)
    try {
      const body: Record<string, string> = { name, email, current_password: currentPassword }
      if (password) { body.password = password; body.password_confirmation = passwordConfirmation }
      const res = await fetch('/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ user: body }),
      })
      if (res.ok) {
        notify('Saved:Profile updated')
        setPassword(''); setPasswordConfirmation(''); setCurrentPassword('')
      } else {
        const data = await res.json().catch(() => ({}))
        setProfileErrors(data.errors || ['Failed to update'])
      }
    } catch { setProfileErrors(['Network error']) }
    setProfileSaving(false)
  }

  // ─── Save notifications ─────────────────────
  const saveNotifications = async () => {
    setNotifSaving(true)
    try {
      const res = await fetch('/profile/notifications', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ order_statuses: orderStatuses, vendor_po_statuses: vendorPoStatuses, customer_po_statuses: customerPoStatuses }),
      })
      if (res.ok) notify('Saved:Notification settings updated')
    } catch { /* silent */ }
    setNotifSaving(false)
  }

  // ─── Render ─────────────────────────────────
  return (
    <>
      <Head title="My Profile" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />

      <div className="divide-y divide-zinc-200 dark:divide-zinc-700" data-testid="profile-page">

        {/* ═══ Profile Section ═══════════════════ */}
        <div className="grid grid-cols-1 gap-x-8 gap-y-8 py-8 md:grid-cols-3">
          <div>
            <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Profile</h2>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">Your account information and credentials.</p>
            <div className="mt-4 flex items-center gap-3">
              <UserCircleIcon className="h-12 w-12 text-zinc-300 dark:text-zinc-600" />
              <div>
                <div className="text-sm font-medium text-zinc-900 dark:text-white">{user.name || user.email}</div>
                <Badge color="indigo">{user.role}</Badge>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10 md:col-span-2">
            <div className="p-6">
              {profileErrors.length > 0 && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20" data-testid="profile-errors">
                  <ul className="list-disc pl-4 text-sm text-red-700 dark:text-red-400">{profileErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
              )}
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">Name</label>
                  <FormInput value={name} onChange={e => setName((e.target as HTMLInputElement).value)} placeholder="Your name" />
                </div>
                <div className="sm:col-span-3">
                  <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">Email</label>
                  <FormInput type="email" value={email} onChange={e => setEmail((e.target as HTMLInputElement).value)} placeholder="your@email.com" />
                </div>
                <div className="col-span-full">
                  <p className="text-xs text-zinc-400 dark:text-zinc-500">Leave password fields blank to keep current password.</p>
                </div>
                <div className="sm:col-span-3">
                  <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">New Password</label>
                  <FormInput type="password" value={password} onChange={e => setPassword((e.target as HTMLInputElement).value)} placeholder="New password..." />
                </div>
                <div className="sm:col-span-3">
                  <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">Confirm Password</label>
                  <FormInput type="password" value={passwordConfirmation} onChange={e => setPasswordConfirmation((e.target as HTMLInputElement).value)} placeholder="Confirm password..." />
                </div>
                <div className="sm:col-span-4">
                  <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">Current Password *</label>
                  <FormInput type="password" value={currentPassword} onChange={e => setCurrentPassword((e.target as HTMLInputElement).value)} placeholder="Required to confirm changes" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-x-3 border-t border-zinc-200 px-6 py-4 dark:border-zinc-700">
              <Button disabled={profileSaving} onClick={saveProfile} data-testid="save-profile">
                {profileSaving ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </div>
        </div>

        {/* ═══ Notifications Section ═════════════ */}
        <div className="grid grid-cols-1 gap-x-8 gap-y-8 py-8 md:grid-cols-3" data-testid="notifications-section">
          <div>
            <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Notifications</h2>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">Choose which status changes trigger email notifications.</p>
          </div>

          <div className="rounded-xl bg-white shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10 md:col-span-2">
            <div className="p-6 space-y-6">
              {/* Order Status */}
              <fieldset>
                <legend className="text-sm font-semibold text-zinc-900 dark:text-white">Order Status Changes</legend>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {Object.entries(orderStatuses).map(([key, val]) => (
                    <div key={key} onClick={() => setOrderStatuses(prev => ({ ...prev, [key]: !prev[key] }))} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-800">
                      <span onClick={e => e.stopPropagation()}><FormCheckbox checked={!!val} onChange={v => setOrderStatuses(prev => ({ ...prev, [key]: v }))} /></span>
                      {humanize(key)}
                    </div>
                  ))}
                </div>
              </fieldset>

              {/* Vendor PO Status */}
              <fieldset>
                <legend className="text-sm font-semibold text-zinc-900 dark:text-white">Vendor PO Status Changes</legend>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {Object.entries(vendorPoStatuses).map(([key, val]) => (
                    <div key={key} onClick={() => setVendorPoStatuses(prev => ({ ...prev, [key]: !prev[key] }))} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-800">
                      <span onClick={e => e.stopPropagation()}><FormCheckbox checked={!!val} onChange={v => setVendorPoStatuses(prev => ({ ...prev, [key]: v }))} /></span>
                      {humanize(key)}
                    </div>
                  ))}
                </div>
              </fieldset>

              {/* Customer PO Status */}
              <fieldset>
                <legend className="text-sm font-semibold text-zinc-900 dark:text-white">Customer PO Status Changes</legend>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {Object.entries(customerPoStatuses).map(([key, val]) => (
                    <div key={key} onClick={() => setCustomerPoStatuses(prev => ({ ...prev, [key]: !prev[key] }))} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-800">
                      <span onClick={e => e.stopPropagation()}><FormCheckbox checked={!!val} onChange={v => setCustomerPoStatuses(prev => ({ ...prev, [key]: v }))} /></span>
                      {humanize(key)}
                    </div>
                  ))}
                </div>
              </fieldset>
            </div>
            <div className="flex items-center justify-end gap-x-3 border-t border-zinc-200 px-6 py-4 dark:border-zinc-700">
              <Button disabled={notifSaving} onClick={saveNotifications} data-testid="save-notifications">
                {notifSaving ? 'Saving...' : 'Save Notifications'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

ProfileIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
